import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Row, Col, Card, CardHeader, CardBody,
  Spinner, Alert, Input, Button,
} from "reactstrap";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

import UserDetailModal from "../modals/users/UserDetailsModal";
import UserDeleteModal from "../modals/users/UserDeleteModal";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from "../../api/urls";
import useAnimatedPlaceholder, { SEARCH_FIELDS } from "../../hooks/useAnimatedPlaceholder";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TruncatedText from "../../components/Common/TruncatedText";
import StatCards, { STAT_ACCENTS } from "../../components/Common/StatCards";
import { downloadCsv } from "../../utils/downloadCsv";
import { RoleBadges } from "../../constants/roleDisplay";

/** Özet kartları için tek seferde okunacak azami kayıt. */
const SUMMARY_MAX_ROWS = 5000;
/** "Son 30 Gün" kartının penceresi. */
const NEW_USER_WINDOW_DAYS = 30;

const EMPTY_SUMMARY = { total: 0, active: null, passive: null, recent: null };

/** Kart değerleri kullanıcı satırlarından hesaplanır; sunucuda hazır sayaç yok. */
const computeSummary = (rows, total, complete) => {
    if (!complete) return { ...EMPTY_SUMMARY, total };

    const cutoff = Date.now() - NEW_USER_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    let active = 0;
    let recent = 0;

    rows.forEach((u) => {
        if (u.enabled) active += 1;
        if (u.createdAt && new Date(u.createdAt).getTime() >= cutoff) recent += 1;
    });

    return { total, active, passive: total - active, recent };
};

const TableCard = ({ className = "", children, ...rest }) => (
    <Card
        className={`rounded-4 border-0 shadow-lg ${className}`}
        style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
        {...rest}
    >
      {children}
    </Card>
);

const useDebouncedValue = (value, delay = 500) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const formatDateTR = (d) =>
    d ? new Date(d).toLocaleDateString("tr-TR", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit"
    }) : "-";

const NAVY = "#221C5D";
const ORANGE = "#E56A22";

const PaginationBar = ({
                         currentPage,
                         totalPages,
                         totalElements,
                         pageSize,
                         onPageChange,
                         onPageSizeChange,
                       }) => {
  const getPages = () => {
    const arr = [];
    const max = 5;
    let start = Math.max(0, currentPage - Math.floor(max / 2));
    let end = Math.min(totalPages - 1, start + max - 1);
    if (end - start + 1 < max) start = Math.max(0, end - max + 1);
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  };

  if (!totalElements) return null;

  return (
      <div className="d-flex justify-content-between align-items-center mt-3 pagination-bar">
        <div className="text-muted" style={{ fontSize: ".9rem" }}>
          {totalElements} kayıttan{" "}
          {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalElements)}{" "}
          arası gösteriliyor
        </div>

        <div className="d-flex align-items-center gap-3 flex-wrap justify-content-end">
          {/* "Sayfa başına" tek satır & ok çakışması yok */}
          <div className="d-flex align-items-center gap-2 page-size-wrap">
            <span className="text-muted" style={{ fontSize: ".9rem" }}>Sayfa başına</span>
            <select
                className="form-select form-select-sm rounded-pill page-size-select"
                value={pageSize}
                onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
            >
              {[10, 25, 50, 100].map(s => (
                  <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <nav>
            <ul className="pagination pagination-sm mb-0 corporate-pagination">
              <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
                <button
                    className="page-link"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 0}
                >
                  <i className="bx bx-chevron-left" />
                </button>
              </li>
              {getPages().map(p => (
                  <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                    <button className="page-link" onClick={() => onPageChange(p)}>
                      {p + 1}
                    </button>
                  </li>
              ))}
              <li className={`page-item ${currentPage >= totalPages - 1 ? "disabled" : ""}`}>
                <button
                    className="page-link"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                >
                  <i className="bx bx-chevron-right" />
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
  );
};

const UsersTable = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 500);
  const [userType, setUserType] = useState("");
  const [companies, setCompanies] = useState([]);
  // Firma filtresi URL'den beslenir (Firma Detay → "Kullanıcıları Gör" → /users?companyId=<uuid>).
  // Ayrı bir state değil; select'in kullandığı state'in başlangıç değeri.
  const [searchParams, setSearchParams] = useSearchParams();
  const [companyId, setCompanyId] = useState(searchParams.get("companyId") || "");
  const [accounts, setAccounts] = useState([]);
  // Cari filtresi de URL'den beslenir (Cariler → "Kullanıcıları Gör" → /users?accountId=<uuid>).
  const [accountId, setAccountId] = useState(searchParams.get("accountId") || "");

  // Özet kartları (listeyle aynı filtrelerden hesaplanır) ve dışa aktarma
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [editingUser, setEditingUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailUser, setDetailUser] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [switchingUserId, setSwitchingUserId] = useState(null);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const animatedPlaceholder = useAnimatedPlaceholder(SEARCH_FIELDS.users, { prefix: "Ara: " });
  const [isImpersonated, setIsImpersonated] = useState(false);
  const [impersonatedUsername, setImpersonatedUsername] = useState("");
  const { roles, loadingUser } = useAuth();
  const isSuperAdmin = roles.includes("ROLE_SUPER_ADMIN");

  const styles = `
    /* ── Tablo temeli ── */
    .users-modern .table thead tr { background-color: #f9f9f9; }
    .users-modern .table th { border: 0; color: #6c757d; font-weight: 600; }
    .users-modern .table td { vertical-align: middle; }
    .users-modern .table-hover tbody tr:hover { background: #fcfcfd; }
    /* ── Butonlar ── */
    .users-modern .btn-rounded { border-radius: 999px; }
    .users-modern .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
    .users-modern .btn-navy:hover { filter: brightness(1.03); }
    .users-modern .btn-orange { background: ${ORANGE}; border-color: ${ORANGE}; color: #fff; }
    .users-modern .btn-orange:hover { filter: brightness(1.03); }
    .users-modern .btn-red { background: #dc3545; border-color: #dc3545; color: #fff; }
    .users-modern .btn-red:hover { filter: brightness(1.03); }
    .users-modern .action-btn { border-radius: 999px; padding: .35rem .6rem; }
    /* ── Kurumsal pagination ── */
    .users-modern .corporate-pagination .page-link { color: ${NAVY}; border-color: ${ORANGE}; }
    .users-modern .corporate-pagination .page-item.active .page-link {
      background-color: ${NAVY}; border-color: ${NAVY}; color: #fff;
    }
    .users-modern .corporate-pagination .page-link:hover { border-color: ${NAVY}; color: ${NAVY}; }
    /* ── Input / select pill ── */
    .users-modern .pill-input, .users-modern .pill-select {
      border-radius: 999px !important;
      padding: .45rem .9rem;
    }
    .users-modern .code-dark { color: #000 !important; }
    /* ── Header filtreleri: flex + wrap, px'siz ── */
    .users-modern .header-filters {
      display: flex;
      flex-wrap: wrap;
      gap: .5rem;
      align-items: center;
      justify-content: flex-end;
    }
    .users-modern .header-filter-select {
      min-width: 140px;
      flex: 1 1 140px;
      max-width: 200px;
    }
    .users-modern .search-input {
      min-width: 160px;
      flex: 2 1 180px;
      max-width: 260px;
    }
    /* ── Pagination ── */
    .users-modern .page-size-wrap { white-space: nowrap; }
    .users-modern .page-size-select { min-width: 70px; padding-right: 1.75rem; }
    .users-modern .pagination-bar { flex-wrap: wrap; gap: .5rem; }
    /* ── Laptop (içerik alanı ~1050–1280px) ── */
    @media (max-width: 1280px) {
      .users-modern .header-filter-select { max-width: 180px; }
      .users-modern .search-input { max-width: 200px; }
    }
    /* ── Tablet / küçük laptop ── */
    @media (max-width: 992px) {
      .users-modern .search-input { max-width: 100%; flex: 1 1 160px; }
      .users-modern .pagination-bar { justify-content: flex-start !important; }
    }
    /* ── Mobil ── */
    @media (max-width: 576px) {
      .users-modern .table { font-size: 12px; }
      .users-modern .table td, .users-modern .table th { padding: .5rem; }
      .users-modern .header-filter-select { max-width: 100%; flex: 1 1 100%; }
      .users-modern .search-input { max-width: 100%; }
    }
  `;

  document.title = "Kullanıcılar | Tahsilat360";

  // fetchers
  const fetchCompanies = async () => {
    try {
      const { data } = await axios.get(API.get_companies_names, { headers: {} });
      if (data.statusCode === 200) setCompanies(data.data);
    } catch {}
  };

  const fetchAccounts = async (compId = "") => {
    try {
      const params = {};
      if (compId) params.companyId = compId;
      const { data } = await axios.get(API.get_accounts_id_name, { params });
      if (data.statusCode === 200) setAccounts(data.data || []);
    } catch {}
  };

  const handleSwitchUser = async (userId, username) => {
    try {
      setSwitchingUserId(userId);

      // Cookie-based auth: backend cookie’yi okur ve yeni access_token cookie set eder
      // Impersonation flag’ini ilk switch’te kaydet (geri dönüş için)
      const existingImpersonatorFlag = localStorage.getItem("impersonatorToken");
      if (!existingImpersonatorFlag) {
        // Gerçek token artık HttpOnly cookie’de; sadece flag bırakıyoruz
        localStorage.setItem("impersonatorToken", "cookie-based");
      }

      const res = await axios.post(API.switch_user(userId), null);

      const resp = res.data;
      if (resp.statusCode === 200) {
        // Backend yeni access_token cookie set etti; tokenExp güncelle
        if (resp.data?.token) {
          try {
            const decoded = jwtDecode(resp.data.token);
            if (decoded.exp) localStorage.setItem("tokenExp", decoded.exp);
          } catch {}
        }

        // Impersonation flag’lerini işaretle
        localStorage.setItem("impersonationActive", "true");
        localStorage.setItem("impersonatedUsername", username || "");
        setIsImpersonated(true);
        setImpersonatedUsername(username || "");
        localStorage.setItem("dashboardNeedsRefresh", "true");
        toast.success(`"${username}" kullanıcısı adına oturum açtınız.`);
        navigate("/dashboard");
      } else {
        toast.error(resp.message || "Switch user işlemi başarısız oldu");
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error("Yetkiniz yok veya oturumunuzun süresi doldu.");
        return navigate("/login");
      }
      toast.error(err.response?.data?.message || "Switch user sırasında bir hata oluştu");
    } finally {
      setSwitchingUserId(null);
    }
  };

  useEffect(() => {
    const imp = localStorage.getItem("impersonationActive") === "true";
    setIsImpersonated(imp);
    if (imp) {
      setImpersonatedUsername(localStorage.getItem("impersonatedUsername") || "");
    }
  }, []);

  /**
   * Filtrelenmiş kullanıcıların tamamını tek istekte getirir.
   *
   * Sunucuda aktif/pasif ya da tarih filtresi olmadığı için özet ve dışa aktarma
   * tüm kümeyi okumak zorunda. `size` üst sınır; sunucu mevcut kadarını döner.
   * Küme sınırı aşarsa satırlar okunmaz, yalnızca toplam bilinir.
   */
  const fetchAllUsers = async (search = "", type = "", compId = "", accId = "") => {
    const params = { page: 0, size: SUMMARY_MAX_ROWS };
    if (search) params.searchTerm = search;
    if (type) params.userType = type;
    if (compId) params.companyId = compId;
    if (accId) params.accountId = accId;

    const res = await axios.get(API.admin_users_list, { params });
    const { content = [], totalElements = 0 } = res.data?.data || {};

    return { rows: content, total: totalElements, complete: content.length >= totalElements };
  };

  const refreshSummary = async (search = "", type = "", compId = "", accId = "") => {
    setSummaryLoading(true);
    try {
      const { rows, total, complete } = await fetchAllUsers(search, type, compId, accId);
      setSummary(computeSummary(rows, total, complete));
    } catch {
      setSummary(EMPTY_SUMMARY);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { rows, complete } = await fetchAllUsers(debouncedSearch, userType, companyId, accountId);

      if (!rows.length) {
        toast.info("Dışa aktarılacak kullanıcı bulunamadı");
        return;
      }

      downloadCsv(
          `kullanicilar-${new Date().toISOString().slice(0, 10)}`,
          ["Kullanıcı Adı", "E-posta", "Telefon", "Firma", "Cari", "Roller", "Durum", "Kayıt Tarihi"],
          rows.map((u) => [
            u.username,
            u.email,
            u.phoneNumber,
            u.companyName,
            u.accountName,
            Array.isArray(u.roles) ? u.roles.join(", ") : u.roles,
            u.enabled ? "Aktif" : "Pasif",
            u.createdAt ? new Date(u.createdAt).toLocaleDateString("tr-TR") : "",
          ])
      );

      if (!complete) {
        toast.warn(`Kayıt sayısı çok yüksek; yalnızca ilk ${rows.length} kullanıcı aktarıldı.`);
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) return navigate("/login");
      toast.error("Dışa aktarma sırasında bir hata oluştu");
    } finally {
      setExporting(false);
    }
  };

  const fetchUsers = async (page = 0, size = 10, search = "", type = "", compId = "", accId = "", first = false) => {
    try {
      setError(null);
      if (first) setLoading(true); else setRefreshing(true);

      const params = { page, size };
      if (search) params.searchTerm = search;
      if (type) params.userType = type;
      if (compId) params.companyId = compId;
      if (accId) params.accountId = accId;

      const res = await axios.get(API.admin_users_list, {
        params,
        headers: {
          "Content-Type": "application/json",
        },
      });

      const { content, totalElements, totalPages, page: pageIdx, size: sz } = res.data.data;
      setUsers(content);
      setTotalElements(totalElements);
      setTotalPages(totalPages);
      setCurrentPage(pageIdx);
      setPageSize(sz);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) return navigate("/login");
      setError(err.response?.data?.message || "Kullanıcıları getirirken bir hata oluştu");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
    // URL'den firma geldiyse cari listesi de o firmaya göre yüklensin.
    fetchAccounts(companyId);
    fetchUsers(0, pageSize, "", userType, companyId, accountId, true);
    refreshSummary("", userType, companyId, accountId);
  }, []);

  useEffect(() => {
    fetchUsers(0, pageSize, debouncedSearch, userType, companyId, accountId);
    refreshSummary(debouncedSearch, userType, companyId, accountId);
  }, [debouncedSearch]);

  const handleCompanyChange = (id) => {
    setCompanyId(id);
    setAccountId("");        // firma değişince cari seçimi sıfırla
    setCurrentPage(0);
    // Seçim URL'e yazılır: sayfa yenilenince/link paylaşılınca aynı liste açılır,
    // filtre temizlenince param da düşer. Diğer query paramlar korunur.
    const nextParams = new URLSearchParams(searchParams);
    if (id) nextParams.set("companyId", id); else nextParams.delete("companyId");
    nextParams.delete("accountId");   // cari seçimi sıfırlandığı için URL'de de kalmamalı
    setSearchParams(nextParams, { replace: true });
    fetchAccounts(id);       // o firmaya ait carileri yükle
    fetchUsers(0, pageSize, debouncedSearch, userType, id, "");
    refreshSummary(debouncedSearch, userType, id, "");
  };
  const handleAccountChange = (id) => {
    setAccountId(id);
    setCurrentPage(0);
    // Firma seçimiyle aynı davranış: seçim URL'e yazılır, temizlenince param düşer.
    const nextParams = new URLSearchParams(searchParams);
    if (id) nextParams.set("accountId", id); else nextParams.delete("accountId");
    setSearchParams(nextParams, { replace: true });
    fetchUsers(0, pageSize, debouncedSearch, userType, companyId, id);
    refreshSummary(debouncedSearch, userType, companyId, id);
  };
  const handleUserTypeChange = (type) => {
    setUserType(type);
    setCurrentPage(0);
    fetchUsers(0, pageSize, debouncedSearch, type, companyId, accountId);
    refreshSummary(debouncedSearch, type, companyId, accountId);
  };
  const handlePageChange = (p) => {
    if (p >= 0 && p < totalPages) {
      setCurrentPage(p);
      fetchUsers(p, pageSize, debouncedSearch, userType, companyId, accountId);
    }
  };
  const handlePageSizeChange = (sz) => {
    setPageSize(sz);
    setCurrentPage(0);
    fetchUsers(0, sz, debouncedSearch, userType, companyId, accountId);
  };
  const summaryItems = useMemo(() => {
    // Küme çok büyükse sayılmadı demektir; 0 yerine "—" gösterilir
    const show = (v) => (v === null || v === undefined ? "—" : v);

    return [
      { key: "total", label: "Toplam Kullanıcı", value: summary.total, hint: "Tüm hesaplar", accent: STAT_ACCENTS.navy },
      { key: "active", label: "Aktif", value: show(summary.active), hint: "Oturum açabilir", accent: STAT_ACCENTS.green },
      { key: "passive", label: "Pasif", value: show(summary.passive), hint: "Devre dışı hesap", accent: STAT_ACCENTS.amber },
      { key: "recent", label: "Son 30 Gün", value: show(summary.recent), hint: "Son 30 günde eklendi", accent: STAT_ACCENTS.orange },
    ];
  }, [summary]);

  const refreshCurrent = () => {
    fetchUsers(currentPage, pageSize, debouncedSearch, userType, companyId, accountId);
    refreshSummary(debouncedSearch, userType, companyId, accountId);
  };

  return (
      <div className="page-content users-modern">
        <style>{styles}</style>
        <div className="container-fluid">
          {/* Sayfa başlığı ve üst aksiyonlar */}
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
            <div>
              <h4 className="mb-1 fw-bold">Kullanıcı Yönetimi</h4>
              <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                Sistemdeki tüm kullanıcıları görüntüleyin ve yönetin
              </p>
            </div>

            <div className="d-flex align-items-center gap-2">
              <Button
                  color="light"
                  className="d-flex align-items-center gap-1 btn-rounded"
                  onClick={handleExport}
                  disabled={exporting || !totalElements}
                  title="Filtrelenmiş kullanıcıları CSV olarak indirir"
              >
                {exporting
                    ? <Spinner size="sm" style={{ width: 14, height: 14 }} />
                    : <i className="bx bx-download" />}
                {" "}Dışa Aktar
              </Button>

              <Button
                  style={{ backgroundColor: NAVY, borderColor: NAVY }}
                  onClick={() => navigate("/users/create")}
                  className="d-flex align-items-center gap-1 btn-rounded"
              >
                <i className="bx bx-plus" /> Kullanıcı Ekle
              </Button>
            </div>
          </div>

          {/* Özet kartları — listeyle aynı filtrelerden hesaplanır */}
          <StatCards className="mb-3" loading={summaryLoading} items={summaryItems} />

          <Row>
            <Col style={{ minWidth: 0 }}>
              <TableCard>
                <CardHeader className="bg-white border-0 rounded-top-4">
                  {/* Desktop header: başlık + ekle butonu üstte, filtreler altta — her ikisi de wrap */}
                  <div className="d-none d-md-block">
                    {/* Filtreler */}
                    <div className="header-filters">
                      <select
                          className="form-select pill-select header-filter-select"
                          value={companyId}
                          onChange={(e) => handleCompanyChange(e.target.value)}
                      >
                        <option value="">Tüm Firmalar</option>
                        {companies.map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
                      </select>

                      <select
                          className="form-select pill-select header-filter-select"
                          value={accountId}
                          onChange={(e) => handleAccountChange(e.target.value)}
                      >
                        <option value="">Tüm Cariler</option>
                        {accounts.map(a => (
                          <option key={a.id} value={a.id}>{a.accountName}</option>
                        ))}
                      </select>

                      <select
                          className="form-select pill-select header-filter-select"
                          value={userType}
                          onChange={(e) => handleUserTypeChange(e.target.value)}
                      >
                        <option value="">Tüm Kullanıcılar</option>
                        <option value="COMPANY">Firma Kullanıcıları</option>
                        <option value="ACCOUNT">Cari/Bayi Kullanıcıları</option>
                      </select>

                      <Input
                          innerRef={searchRef}
                          type="search"
                          placeholder={animatedPlaceholder}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="search-input pill-input"
                          inputMode="search"
                          autoComplete="off"
                          autoCorrect="off"
                          spellCheck={false}
                      />
                    </div>
                  </div>

                  {/* MOBILE header — sıra korunarak */}
                  <div className="d-md-none">
                    <div className="d-flex flex-column gap-2">
                      <Input
                          innerRef={searchRef}
                          type="search"
                          placeholder={animatedPlaceholder}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pill-input"
                          inputMode="search"
                          autoComplete="off"
                          autoCorrect="off"
                          spellCheck={false}
                      />
                      <div className="d-flex gap-2 flex-wrap">
                        <select
                            className="form-select pill-select"
                            value={companyId}
                            onChange={(e) => handleCompanyChange(e.target.value)}
                        >
                          <option value="">Tüm Firmalar</option>
                          {companies.map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
                        </select>
                        <select
                            className="form-select pill-select"
                            value={accountId}
                            onChange={(e) => handleAccountChange(e.target.value)}
                        >
                          <option value="">Tüm Cariler</option>
                          {accounts.map(a => <option key={a.id} value={a.id}>{a.accountName}</option>)}
                        </select>
                        <select
                            className="form-select pill-select"
                            value={userType}
                            onChange={(e) => handleUserTypeChange(e.target.value)}
                        >
                          <option value="">Tüm Kullanıcılar</option>
                          <option value="COMPANY">Firma</option>
                          <option value="ACCOUNT">Cari/Bayi</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className="pt-2">
                  {error && (
                      <Alert color="danger" className="mb-3 rounded-4">
                        <i className="bx bx-error-circle me-2" />
                        {error}
                        <button
                            className="btn btn-sm btn-outline-danger ms-2 btn-rounded"
                            onClick={() => fetchUsers(currentPage, pageSize, debouncedSearch, userType, companyId, accountId)}
                        >
                          Tekrar Dene
                        </button>
                      </Alert>
                  )}

                  {loading ? (
                      <div className="text-center p-5">
                        <Spinner size="lg" color="primary" />
                        <h6 className="mt-3 text-muted">Kullanıcılar yükleniyor...</h6>
                      </div>
                  ) : (
                      <>
                        <div className={`d-flex justify-content-between align-items-center mb-2 ${refreshing ? 'opacity-50' : ''}`}>
                          <div className="text-muted" style={{ fontSize: ".9rem" }}>
                            {totalElements > 0 && (
                                <>Toplam <strong>{totalElements}</strong> kayıt • Sayfa <strong>{currentPage + 1}</strong> / {totalPages}</>
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <button
                                className="btn btn-light btn-sm btn-rounded"
                                onClick={() => fetchUsers(currentPage, pageSize, debouncedSearch, userType, companyId, accountId)}
                                title="Yenile"
                            >
                              <i className={`bx bx-refresh ${refreshing ? 'bx-spin' : ''}`} />
                            </button>
                          </div>
                        </div>

                        <div className="table-responsive rounded-4 border">
                          <Table className="table table-hover mb-0">
                            <Thead>
                              <Tr>
                                <Th>Ad Soyad</Th>
                                <Th className="d-none d-md-table-cell">E-posta</Th>
                                <Th className="d-none d-sm-table-cell">Roller</Th>
                                <Th className="d-none d-lg-table-cell">Firma Adı</Th>
                                <Th className="d-none d-xl-table-cell">Cari/Bayi Adı</Th>
                                <Th>Durum</Th>
                                <Th className="text-end">İşlemler</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {users.length ? users.map(u => (
                                  <Tr key={u.id} className="border-bottom">
                                    <Td>
                                      <TruncatedText
                                          text={u.username}
                                          maxLength={20}
                                          className="fw-semibold text-dark"
                                      />
                                    </Td>
                                    <Td className="d-none d-md-table-cell">
                                      <TruncatedText text={u.email} maxLength={22} />
                                    </Td>
                                    <Td className="d-none d-sm-table-cell"><RoleBadges roles={u.roles || []} /></Td>
                                    <Td className="d-none d-lg-table-cell">
                                      <TruncatedText text={u.companyName} maxLength={18} />
                                    </Td>
                                    <Td className="d-none d-xl-table-cell">
                                      <TruncatedText text={u.accountName} maxLength={18} />
                                    </Td>
                                    <Td>
                               <span className="badge border-0" style={{ borderRadius: 999, backgroundColor: u.enabled ? "#71C30B" : "#E07A7A", color: "#fff" }}>
                                  {u.enabled ? "Aktif" : "Pasif"}
                                </span>
                                    </Td>
                                    <Td className="text-end">
                                      <div className="d-flex gap-1 justify-content-end flex-wrap">
                                        {isSuperAdmin && !u.roles.includes("ROLE_SUPER_ADMIN") && (
                                            <button
                                                className="btn btn-sm action-btn btn-light d-flex align-items-center gap-1"
                                                title="Kullanıcıya Geç"
                                                disabled={switchingUserId === u.id}
                                                onClick={() => handleSwitchUser(u.id, u.username)}
                                            >
                                              {switchingUserId === u.id ? (
                                                  <Spinner size="sm" />
                                              ) : (
                                                  <>
                                                    <i className="bx bx-transfer-alt" />
                                                    <span className="d-none d-md-inline">Kullanıcıya Geç</span>
                                                  </>
                                              )}
                                            </button>
                                        )}
                                        <button
                                            className="btn btn-sm action-btn btn-navy"
                                            title="Düzenle"
                                            onClick={() => navigate(`/users/${u.id}/edit`)}
                                        >
                                          <i className="bx bx-edit" />
                                        </button>
                                        <button
                                            className="btn btn-sm action-btn btn-orange"
                                            title="Detay"
                                            onClick={() => navigate(`/users/${u.id}`)}
                                        >
                                          <i className="bx bx-show" />
                                        </button>

                                        <button
                                            className="btn btn-sm action-btn btn-red"
                                            title="Sil"
                                            onClick={() => { setDeleteUser(u); setDeleteModalOpen(true); }}
                                        >
                                          <i className="bx bx-trash" />
                                        </button>
                                      </div>
                                    </Td>
                                  </Tr>
                              )) : (
                                  <Tr><Td colSpan="10" className="text-center p-4">Kullanıcı bulunamadı</Td></Tr>
                              )}
                            </Tbody>
                          </Table>
                        </div>

                        <PaginationBar
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalElements={totalElements}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                      </>
                  )}
                </CardBody>

                <UserDetailModal
                    isOpen={detailModalOpen}
                    toggle={() => setDetailModalOpen(false)}
                    user={detailUser}
                />
                <UserDeleteModal
                    isOpen={deleteModalOpen}
                    toggle={() => setDeleteModalOpen(false)}
                    user={deleteUser}
                    onConfirm={({ success, message }) => {
                      setDeleteModalOpen(false);
                      (success ? toast.success : toast.error)(message);
                      refreshCurrent();
                    }}
                />
                <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick pauseOnFocusLoss draggable pauseOnHover />
              </TableCard>
            </Col>
          </Row>
        </div>
      </div>
  );
};

export default UsersTable;